import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';

const SOURCE = 'jv-mailbox-bundle-cart-sync';
const BUNDLE_ID_KEY = '_jv_bundle_id';
const BUNDLE_ROLE_KEY = '_jv_bundle_role';

function cartUpdateUrl() {
  const u = Theme.routes.cart_update_url;
  return u.endsWith('.js') ? u : `${u}.js`;
}

function cartChangeUrl() {
  const u = Theme.routes.cart_change_url;
  return u.endsWith('.js') ? u : `${u}.js`;
}

/**
 * @param {Record<string, string>} properties
 * @returns {Record<string, string>}
 */
function propertiesWithoutBundleLink(properties) {
  /** @type {Record<string, string>} */
  const next = {};
  for (const [key, value] of Object.entries(properties || {})) {
    if (key === BUNDLE_ID_KEY || key === BUNDLE_ROLE_KEY) continue;
    if (value == null || String(value).trim() === '') continue;
    next[key] = String(value);
  }
  return next;
}

/**
 * @param {any[]} items
 */
function cartHasBundleLinkProperties(items) {
  return items.some(
    (item) => item.properties?.[BUNDLE_ID_KEY] || item.properties?.[BUNDLE_ROLE_KEY]
  );
}

/**
 * Remove _jv_bundle_* from all cart lines so they are not persisted on the order.
 * Keeps visible line item properties (e.g. Address number).
 * @returns {Promise<boolean>} true if any line was updated
 */
export async function stripBundleLinkPropertiesFromCart() {
  const cartRes = await fetch('/cart.js');
  if (!cartRes.ok) return false;
  const cart = await cartRes.json();
  const items = cart.items || [];
  if (!cartHasBundleLinkProperties(items)) return false;

  let changed = false;
  for (const item of items) {
    const props = item.properties || {};
    if (!props[BUNDLE_ID_KEY] && !props[BUNDLE_ROLE_KEY]) continue;

    const next = propertiesWithoutBundleLink(props);
    const res = await fetch(
      cartChangeUrl(),
      fetchConfig('json', {
        body: JSON.stringify({
          id: item.key,
          properties: next,
        }),
      })
    );
    if (!res.ok) continue;
    changed = true;
  }

  return changed;
}

/**
 * Remove upgrade lines whose bundle no longer has a main line in the cart.
 * @returns {Promise<boolean>} true if cart was modified
 */
async function removeOrphanUpgrades() {
  const cartRes = await fetch('/cart.js');
  const cart = await cartRes.json();
  const items = cart.items || [];

  /** @type {Set<string>} */
  const bundlesWithMain = new Set();
  for (const item of items) {
    const bid = item.properties?.[BUNDLE_ID_KEY];
    const role = item.properties?.[BUNDLE_ROLE_KEY];
    if (bid && role === 'main') {
      bundlesWithMain.add(String(bid));
    }
  }

  /** @type {Record<string, number>} */
  const updates = {};
  for (const item of items) {
    const bid = item.properties?.[BUNDLE_ID_KEY];
    const role = item.properties?.[BUNDLE_ROLE_KEY];
    if (bid && role === 'upgrade' && !bundlesWithMain.has(String(bid))) {
      updates[item.key] = 0;
    }
  }

  if (Object.keys(updates).length === 0) return false;

  const cartItemsComponents = document.querySelectorAll('cart-items-component');
  /** @type {Set<string>} */
  const sectionsToUpdate = new Set();
  cartItemsComponents.forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) {
      sectionsToUpdate.add(el.dataset.sectionId);
    }
  });

  const body = JSON.stringify({
    updates,
    sections: Array.from(sectionsToUpdate).join(','),
    sections_url: window.location.pathname,
  });

  const res = await fetch(cartUpdateUrl(), fetchConfig('json', { body }));
  const text = await res.text();
  const parsed = JSON.parse(text);

  if (!res.ok || parsed.errors) {
    return false;
  }

  if (parsed.sections) {
    for (const sid of sectionsToUpdate) {
      const html = parsed.sections[sid];
      if (html) {
        try {
          await morphSection(sid, html);
        } catch {
          /* section not in DOM (e.g. drawer closed) */
        }
      }
    }
  }

  const itemCount = parsed.item_count ?? 0;

  cartItemsComponents.forEach((comp) => {
    if (!(comp instanceof HTMLElement) || !comp.dataset.sectionId) return;
    comp.dispatchEvent(
      new CartUpdateEvent({}, comp.dataset.sectionId, {
        itemCount: typeof itemCount === 'number' ? itemCount : parseInt(String(itemCount), 10) || 0,
        source: SOURCE,
        sections: parsed.sections,
      })
    );
  });

  return true;
}

let debounceTimer = 0;

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const source = /** @type {any} */ (event).detail?.data?.source;
  if (source === SOURCE) return;
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = window.setTimeout(() => {
    debounceTimer = 0;
    removeOrphanUpgrades().catch(() => {});
  }, 60);
});

/** @type {WeakSet<EventTarget>} */
const retriedCheckoutControls = new WeakSet();

/**
 * @param {Element} el
 */
function isCheckoutControl(el) {
  if (!(el instanceof HTMLElement) || el.hasAttribute('disabled')) return false;
  if (el.matches('button[name="checkout"], input[name="checkout"]')) return true;
  if (el instanceof HTMLAnchorElement && el.href.includes('/checkout')) return true;
  if (el.closest('.additional-checkout-buttons, .shopify-payment-button, shopify-accelerated-checkout-cart')) {
    return true;
  }
  return false;
}

/**
 * @param {MouseEvent} event
 */
async function onCheckoutClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const control = target.closest(
    'button[name="checkout"], input[name="checkout"], a[href*="/checkout"], .additional-checkout-buttons button, .additional-checkout-buttons a, .shopify-payment-button__button, shopify-accelerated-checkout-cart button'
  );
  if (!control || !isCheckoutControl(control)) return;

  if (retriedCheckoutControls.has(control)) {
    retriedCheckoutControls.delete(control);
    return;
  }

  let cart;
  try {
    const cartRes = await fetch('/cart.js');
    cart = await cartRes.json();
  } catch {
    return;
  }

  if (!cartHasBundleLinkProperties(cart.items || [])) return;

  event.preventDefault();
  event.stopPropagation();

  try {
    await stripBundleLinkPropertiesFromCart();
  } catch {
    /* still try to continue checkout */
  }

  if (control instanceof HTMLAnchorElement && control.href) {
    window.location.assign(control.href);
    return;
  }

  if (control.closest('.additional-checkout-buttons, .shopify-payment-button, shopify-accelerated-checkout-cart')) {
    retriedCheckoutControls.add(control);
    control.click();
    return;
  }

  window.location.assign('/checkout');
}

document.addEventListener('click', (event) => {
  if (!(event instanceof MouseEvent)) return;
  onCheckoutClick(event).catch(() => {});
}, false);
